/**
 * Minimal reactive state subject: holds current value, notifies subscribers on set,
 * and synchronously replays the current value to each new subscriber.
 */
export function createState(initial) {
    let value = initial;
    const listeners = new Set();
    return {
        get() {
            return value;
        },
        set(next) {
            value = next;
            for (const fn of [...listeners]) {
                fn(next);
            }
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(value);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}
export function subscribeFiltered(state, predicate, listener) {
    return state.subscribe((v) => {
        if (predicate(v)) {
            listener(v);
        }
    });
}
export function asFilteredSubscribable(state, predicate) {
    return {
        subscribe(listener) {
            return subscribeFiltered(state, predicate, listener);
        },
    };
}
/** Subscribe only when current value is non-null (initial null is skipped). */
export function subscribeNonNull(state, listener) {
    return state.subscribe((v) => {
        if (v !== null) {
            listener(v);
        }
    });
}
export function asNonNullSubscribable(state) {
    return {
        subscribe(listener) {
            return subscribeNonNull(state, listener);
        },
    };
}
/**
 * Emits only when value is defined and has changed since the last emission for that subscriber.
 * Each subscriber tracks its own last-emitted value independently, so a late subscriber
 * still receives the current value on subscribe without suppressing replay for others.
 */
export function asDistinctDefinedSubscribable(state) {
    return {
        subscribe(listener) {
            let lastEmitted;
            return state.subscribe((raw) => {
                if (raw === undefined) {
                    return;
                }
                if (lastEmitted !== undefined && raw === lastEmitted) {
                    return;
                }
                lastEmitted = raw;
                listener(raw);
            });
        },
    };
}
